import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Radio, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLiveArenas } from "@/hooks/useLiveArenas";
import { useAuthContext } from "@/contexts/AuthContext";
import { useMultiLocationPresence, useLocationPresence } from "@/hooks/useLocationPresence";
import { useArenaTopics } from "@/hooks/useArenaTopics";
import ArenaCard from "@/components/live-arenas/ArenaCard";
import ArenaRoomModal from "@/components/live-arenas/ArenaRoomModal";
import BackgroundBalloons from "@/components/pop-balloon/BackgroundBalloons";
import { ArenaHighlightsFeed } from "@/components/live-arenas/ArenaHighlightsFeed";
import { ScheduledArenasList } from "@/components/live-arenas/ScheduledArenasList";
import { ArenaRecordingsList } from "@/components/live-arenas/ArenaRecordingsList";
import { ArenaDiscoveryFilters, ArenaFilters, parseParticipantRange } from "@/components/live-arenas/ArenaDiscoveryFilters";
import { CreateArenaModal, CreateArenaData } from "@/components/live-arenas/CreateArenaModal";
import { TrendingTopicsBar } from "@/components/live-arenas/TrendingTopicsBar";
import { ArenaRecommendations } from "@/components/live-arenas/ArenaRecommendations";

const LiveArenas = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [filters, setFilters] = useState<ArenaFilters>({
    language: null,
    arenaType: null,
    participantRange: null,
  });

  const participantRange = parseParticipantRange(filters.participantRange);

  const {
    arenas,
    loading,
    currentArena,
    participants,
    joinArena,
    leaveArena,
    toggleMute,
    toggleVideo,
    fetchParticipants,
    setCurrentArena,
    createArena,
  } = useLiveArenas(undefined, undefined, {
    language: filters.language,
    arenaType: filters.arenaType,
    minParticipants: participantRange?.min,
    maxParticipants: participantRange?.max,
  });

  const activeFilterCount = [filters.language, filters.arenaType, filters.participantRange].filter(Boolean).length;

  // Arena topics and recommendations
  const {
    trendingTopics,
    recommendations,
    loading: topicsLoading,
    selectedTopics,
    toggleTopicFilter,
    clearTopicFilters,
    addArenaTags,
  } = useArenaTopics();

  const handleCreateArena = useCallback(async (data: CreateArenaData & { tags?: string[] }) => {
    const success = await createArena(data);
    // Add tags if arena was created successfully and tags were provided
    if (success && data.tags && data.tags.length > 0) {
      // We need to get the newly created arena ID - it should be in currentArena
      // This is a simplified approach - in production you'd want to return the ID from createArena
    }
    return success;
  }, [createArena]);

  // Get real-time presence counts for all arenas
  const arenaIds = useMemo(() => arenas.map(a => a.id), [arenas]);
  const { counts: presenceCounts, previewUsers } = useMultiLocationPresence("arena", arenaIds);

  // Track presence in current arena
  const { joinLocation: joinArenaPresence, leaveLocation: leaveArenaPresence } = 
    useLocationPresence("arena", currentArena?.id || null);

  // Join/leave presence when entering/leaving arena
  useEffect(() => {
    if (currentArena) {
      joinArenaPresence();
    }
    return () => {
      leaveArenaPresence();
    };
  }, [currentArena?.id, joinArenaPresence, leaveArenaPresence]);

  // Calculate total live users
  const totalLiveUsers = useMemo(() => 
    Object.values(presenceCounts).reduce((sum, count) => sum + count, 0),
    [presenceCounts]
  );

  // Filter arenas by selected topic tags
  const filteredArenas = useMemo(() => {
    let filtered = arenas;
    
    // Filter by selected trending topics (if any)
    if (selectedTopics.length > 0) {
      // This would need arena tags data - for now filter by topic string match
      filtered = filtered.filter(a => {
        const topicNames = trendingTopics
          .filter(t => selectedTopics.includes(t.interest_id))
          .map(t => t.interest_name.toLowerCase());
        return topicNames.some(name => 
          a.topic.toLowerCase().includes(name) || 
          a.name.toLowerCase().includes(name)
        );
      });
    }
    
    // Also filter by legacy selectedTopic if set
    if (selectedTopic && selectedTopic !== "All") {
      filtered = filtered.filter(a => a.topic === selectedTopic);
    }
    
    return filtered;
  }, [arenas, selectedTopics, selectedTopic, trendingTopics]);

  const handleJoinArena = useCallback(async (arenaId: string) => {
    const arena = arenas.find(a => a.id === arenaId);
    if (arena?.is_joined) {
      setCurrentArena(arena);
      await fetchParticipants(arenaId);
    } else {
      const success = await joinArena(arenaId);
      if (success) {
        const joinedArena = arenas.find(a => a.id === arenaId);
        if (joinedArena) {
          setCurrentArena({ ...joinedArena, is_joined: true });
        }
      }
    }
  }, [arenas, joinArena, fetchParticipants, setCurrentArena]);

  const handleLeaveArena = useCallback(async () => {
    if (currentArena) {
      await leaveArena(currentArena.id);
    }
  }, [currentArena, leaveArena]);

  const handleToggleMute = useCallback(async () => {
    if (currentArena) {
      await toggleMute(currentArena.id);
    }
  }, [currentArena, toggleMute]);

  const handleToggleVideo = useCallback(async () => {
    if (currentArena) {
      await toggleVideo(currentArena.id);
    }
  }, [currentArena, toggleVideo]);

  return (
    <div className="min-h-screen sky-gradient relative overflow-hidden">
      {/* Background */}
      <BackgroundBalloons />

      <div className="relative z-10 flex flex-col min-h-screen pb-20">
        {/* Header */}
        <div className="px-4 pt-4 pb-2">
        <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2">
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
            <div className="flex items-center gap-2">
              <ArenaDiscoveryFilters
                filters={filters}
                onFiltersChange={setFilters}
                activeFilterCount={activeFilterCount}
              />
              <CreateArenaModal onCreateArena={handleCreateArena} />
            </div>
          </div>

          <div className="text-center mt-2">
            <h1 className="text-3xl font-bold text-foreground">Live Arenas</h1>
            <p className="text-muted-foreground text-sm">Join group conversations</p>
          </div>
        </div>

        {/* Stats banner */}
        <div className="mx-4 mt-4 p-4 rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 shadow-md">
          <div className="flex items-center justify-around">
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{arenas.length}</p>
              <p className="text-xs text-muted-foreground">Live Arenas</p>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                </span>
                <p className="text-2xl font-bold text-green-600">{totalLiveUsers}</p>
              </div>
              <p className="text-xs text-muted-foreground">People Live</p>
            </div>
            <div className="w-px h-8 bg-border" />
            <Link to="/vibe-feed" className="text-center group">
              <div className="flex items-center justify-center gap-1">
                <PlayCircle className="w-4 h-4 text-pop group-hover:scale-110 transition-transform" />
                <p className="text-lg font-bold text-pop">Feed</p>
              </div>
              <p className="text-xs text-muted-foreground">Vibe</p>
            </Link>
          </div>
        </div>

        {/* Trending Topics - replaces old static topic filters */}
        <div className="mt-4 px-4">
          <TrendingTopicsBar
            topics={trendingTopics}
            selectedTopics={selectedTopics}
            onTopicToggle={toggleTopicFilter}
            onClearFilters={clearTopicFilters}
            loading={topicsLoading}
          />
        </div>

        {/* Personalized Recommendations */}
        {user && recommendations.length > 0 && (
          <div className="px-4 mt-4">
            <ArenaRecommendations
              recommendations={recommendations}
              onJoinArena={handleJoinArena}
              loading={topicsLoading}
            />
          </div>
        )}

        {/* Highlights Feed */}
        <div className="px-4 mt-4">
          <ArenaHighlightsFeed />
        </div>

        {/* Scheduled Arenas */}
        <div className="px-4 mt-6">
          <ScheduledArenasList />
        </div>

        {/* Past Recordings */}
        <div className="px-4 mt-6">
          <ArenaRecordingsList />
        </div>

        {/* Arenas grid */}
        <div className="flex-1 overflow-y-auto px-4 mt-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Radio className="w-5 h-5 text-pop" />
            Live Now
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 rounded-full border-4 border-pop border-t-transparent animate-spin" />
            </div>
          ) : filteredArenas.length === 0 ? (
            <div className="text-center py-12">
              <Radio className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No live arenas found</p>
              <p className="text-sm text-muted-foreground mt-1">Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-8">
              {filteredArenas.map((arena) => (
                <ArenaCard
                  key={arena.id}
                  arena={{
                    ...arena,
                    participants_count: presenceCounts[arena.id] || arena.participants_count,
                  }}
                  onJoin={() => handleJoinArena(arena.id)}
                  previewFaces={previewUsers[arena.id]?.map(u => u.photo_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Arena Room Modal */}
      <ArenaRoomModal
        arena={currentArena}
        participants={participants}
        currentUserId={user?.id}
        isOpen={!!currentArena}
        onClose={() => setCurrentArena(null)}
        onLeave={handleLeaveArena}
        onToggleMute={handleToggleMute}
        onToggleVideo={handleToggleVideo}
      />
    </div>
  );
};

export default LiveArenas;
